/**
 * CreatorTeamSection — a publisher's official creator program, consolidated.
 *
 * Built to `devourplay-anchor/Drop-In/team-drops/publisher-drop.html`
 * (Official Creator Team). This is the section the whole publisher pitch rests
 * on, because it is the one thing no streaming platform can render:
 *
 *   · Twitch, YouTube and Kick side by side. No platform will ever surface a
 *     rival's stream — a permanent conflict of interest, not a feature gap.
 *   · An "official" mark. Twitch Teams is retired, so no platform has an
 *     endorsed-by-the-publisher primitive left.
 *   · "Live" meaning live ON THIS TITLE, so a member streaming a competitor
 *     never appears on the publisher's own page.
 *
 * All filtering happens server-side in /drops/slug/:slug/roster — the client
 * renders what it is given and never re-derives liveness, so the page and the
 * counts can never disagree.
 */

import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useGetDropRoster } from "@/hooks/drop/useGetDropRoster";
/* The generated type, not a local mirror — the client already exports it. */
import { type RosterHighlight } from "@devour/client";
import {CreatorRosterCard} from "./CreatorRosterCard";
import {CreatorTheaterPane, type TheaterPane} from "./CreatorTheaterPane";
import {
    type RosterMember,
    compact,
    formatDuration,
    highlightForMember,
    memberKey,
    playerSource,
} from "./rosterMember";
import "./CreatorTeamSection.scss";

interface CreatorTeamSectionProps {
    slug: string;
    /** Signed-out click target for the follow. */
    onLoginRequired?: () => void;
    referralUserId?: string;
    /** Section label from the Drop's config — "Creator Team", "Roster", … */
    title?: string;
    /**
     * The title's own art and trailer, used when the player has nothing else.
     *
     * A publisher Drop spends most of its life before launch: nobody live, and
     * possibly no clips of the title yet. The pane still has to be the title's
     * own footage rather than a player-shaped hole, so it falls back to key art
     * with the trailer as the play target.
     */
    fallbackPosterUrl?: string;
    fallbackVideoUrl?: string;
}

export function CreatorTeamSection({
    slug, title = "Official Creator Team", fallbackPosterUrl, fallbackVideoUrl,
    onLoginRequired, referralUserId,
}: CreatorTeamSectionProps): React.ReactElement {
    const {data, isLoading, isError} = useGetDropRoster(slug);
    const [selectedKey, setSelectedKey] = useState<string>();
    /*
     * Which clip is in the theater. Undefined means "the top one" rather than
     * "none", so the pane is never empty while highlights exist.
     */
    const [selectedHighlightId, setSelectedHighlightId] = useState<string>();

    if (isLoading) {
        return (
            <section className="creator-team">
                <Skeleton height={220} count={2} />
            </section>
        );
    }

    /*
     * A failed fetch must not read as an empty roster. Without this the
     * fallback to `[]` renders "No creators on this team yet." — which tells a
     * publisher their team is empty when in fact we could not load it, and is
     * the single most misleading thing this section could say.
     */
    if (isError) {
        return (
            <section className="creator-team" aria-label={title}>
                <div className="creator-team__empty">
                    Couldn’t load the creator team just now. Refresh to try again.
                </div>
            </section>
        );
    }

    const members: RosterMember[] = data?.members ?? [];
    /*
     * Clips of this title, sent only when nobody is live. They are what stops
     * the theater being an empty pane for the eight months before a game ships
     * — the state this page spends most of its life in.
     */
    const highlights: RosterHighlight[] = data?.highlights ?? [];
    /*
     * `filterGameNames`, `filterMode`, `hiddenOffGame` and `activeCampaignWindow`
     * still come back on this response and are still the reason the roster
     * below is filtered the way it is — they are simply not rendered any more.
     * The admin roster editor is where that policy is stated, to the person who
     * sets it. See the note where the bars used to be.
     */
    /*
     * The platforms this roster is actually on, in a stable order. Both the
     * header note and the footer claim used to name Twitch, YouTube and Kick
     * unconditionally — including on a roster that has no YouTube member at
     * all, which is a claim the page cannot support.
     */
    const PLATFORM_ORDER = ["TWITCH", "YOUTUBE", "KICK"];
    const PLATFORM_LABELS: Record<string, string> = {TWITCH: "Twitch", YOUTUBE: "YouTube", KICK: "Kick"};
    const rosterPlatforms = PLATFORM_ORDER
        .filter(p => members.some(m => String(m.platform).toUpperCase() === p))
        .map(p => PLATFORM_LABELS[p]);

    /*
     * Nothing plays until someone picks a creator. No auto-selection: the page
     * has its own trailer above, and two things playing at once on load is
     * worse than one click.
     */
    /*
     * The selection is re-checked against live state on every poll, not just
     * at click time. The roster refetches every 60s; a creator who goes
     * offline while being watched stays in `members` (only filtered-out
     * members are dropped), so without the liveness gate the player would keep
     * showing an offline channel while their own card had already flipped to
     * "Offline".
     */
    const selected = members.find(m => memberKey(m) === selectedKey && m.isLive && !m.offGame);
    const selectedSource = selected ? playerSource(selected) : undefined;

    /*
     * The clip the theater falls back to.
     *
     * `selectedHighlightId` is the operator of this section picking a clip from
     * the row; with nothing picked it defaults to the first, which is the
     * highest-viewed. So the theater has something in it the moment the section
     * renders, without anyone clicking — but only when nobody is live, because
     * the server only sends highlights then.
     *
     * A live member always wins. `selected` is checked first everywhere below,
     * so clicking a creator who has just come online replaces the clip rather
     * than competing with it.
     */
    const activeHighlight = highlights.find(h => h.id === selectedHighlightId) ?? highlights[0];

    /*
     * Exactly one pane, decided in ONE place.
     *
     * These were three independent conditions, which is how the theater became
     * able to render NOTHING at all: nobody live, no highlights and no key art
     * left all three false and the left column went blank beside a populated
     * rail. That is not an edge case — esports, brand, community and agency
     * rosters carry no `release`, so they have no key art to fall back to and
     * hit it as their ordinary state. A live member whose player source
     * disappeared between polls (live on YouTube, channel known but broadcast
     * id not) did the same thing from the other direction: `selected` stayed
     * truthy and suppressed the other two panes while its own could not render.
     *
     * Deriving one value makes the fallback structural — there is no
     * combination of inputs that resolves to no pane.
     */
    const pane: TheaterPane = selected && selectedSource
        ? "live"
        : activeHighlight
            ? "clip"
            : fallbackPosterUrl
                ? "poster"
                : "bare";

    return (
        <section className="creator-team" aria-label={title}>
            {/*
              * Section header, per the anchor's `.sec__hd` (lines 538-545).
              * The note is the scale of the program and the platforms it spans
              * — "one view, which no platform will ever build" is the claim
              * this whole section makes, and it needs stating at the top as
              * well as the bottom.
              */}
            <div className="creator-team__head">
                <div className="creator-team__title-row">
                    <h3 className="creator-team__title">{title}</h3>
                    {/*
                      * Only the live count, and only when it is not zero.
                      *
                      * The roster size and the platform list are both visible in
                      * the rail directly beneath — every row carries its own
                      * platform — so stating "5 official · on Twitch" above them
                      * was counting and naming what the reader can already see.
                      * A live count is the one thing the rail cannot show at a
                      * glance, and at zero even that is noise.
                      */}
                    {(data?.liveOnGame ?? 0) > 0 &&
                        <span className="creator-team__note">
                            <b>{data?.liveOnGame}</b> live now
                        </span>
                    }
                </div>
            </div>

            {/*
              * The roster filter bar and the campaign-window band used to
              * render here, straight from the anchor.
              *
              * They are annotation, not product. Both explain OUR mechanics to
              * the reader of a design document — which games count as live, how
              * many members are being hidden, that a time-boxed window is
              * widening the categories. A visitor to a publisher's page did not
              * come to learn our filtering policy, and "2 members are live on
              * other titles — hidden" tells them about creators they cannot see
              * while advertising to the publisher that their own roster is
              * being suppressed.
              *
              * The filtering still happens — it is applied server-side in
              * /drops/slug/:slug/roster and is the whole guarantee the roster
              * makes. It simply no longer narrates itself. The place for the
              * rule, the hidden count and the window is the admin roster
              * editor, where the person who sets the policy can see its effect.
              */}
            {/*
              * The team-wide follow bar was here, per the anchor's `.teambar`
              * (lines 571-576). Removed 2026-08-12, for two reasons that
              * compound:
              *
              *   · It was the SAME control as the hero's follow, bound to the
              *     same `slug` — one Drop follow, rendered twice on one page.
              *   · Its label ("one follow for the whole team — every official
              *     creator, on every platform") described something it does not
              *     do. Following the title's Drop does not subscribe you to the
              *     creators on it, and nothing dispatches on `stream.online`
              *     anyway. It also assumed a want people do not have: a visitor
              *     here for one creator is not asking for all of them.
              *
              * Following an individual creator belongs on that creator's row,
              * not on the section.
              */}

            {/*
              * Theater split: player left, roster as a rail on the right.
              *
              * The first cut followed the anchor — full-width player ABOVE the
              * grid. At content width a 16/9 player is around a thousand pixels
              * tall, so it filled the viewport on its own and pushed the entire
              * roster below the fold. The moment you used the feature, the
              * thing this surface exists to prove — one view of everyone across
              * three platforms — was off screen.
              *
              * Side by side, watching and browsing are the same motion: the
              * next creator is always a glance and one click away, and the
              * player never goes away to get there. Under 1000px there is no
              * room for two columns, so it stacks back to player-over-grid.
              */}
            {/*
              * The theater is the LAYOUT, not a state.
              *
              * It used to engage only while something was playing and fall back
              * to a 4-up grid otherwise — so the section changed shape under the
              * reader depending on whether a creator happened to be streaming.
              * Player left, roster rail right, always; the grid is only the
              * narrow-viewport stack. The player always resolves to something:
              * a live on-game creator, else a clip of the title or franchise,
              * else the title's own key art.
              */}
            {/*
              * No roster at all ⇒ no theater.
              *
              * The theater is deliberately never blank — it falls back to a
              * clip, then to the title's key art, so the section still reads
              * during the months before a launch when nobody is live. That
              * reasoning assumes there IS a team. With zero members it produced
              * a 16/9 pane of key art, a full viewport tall on a wide screen,
              * beside one unstyled line of text; the page appeared to have
              * loaded a broken video player.
              *
              * A band instead. It says the same thing in the space the sentence
              * actually needs, and the section stops claiming to be a stage.
              */}
            {members.length === 0
                ? <div className="creator-team__empty">
                    No creators on this team yet.
                </div>
                : <div className="creator-team__theater creator-team__theater--split">
                    <CreatorTheaterPane
                        pane={pane}
                        selected={selected}
                        selectedSource={selectedSource}
                        activeHighlight={activeHighlight}
                        fallbackPosterUrl={fallbackPosterUrl}
                        fallbackVideoUrl={fallbackVideoUrl}
                        hasMembers={members.length > 0}
                        onClosePlayer={() => setSelectedKey(undefined)}
                    />

                    {/*
                      * Beside a player the roster is a RAIL, not the grid.
                      *
                      * It was the same 16:9 cards at one per column, so four
                      * members filled the player's height and the rest scrolled
                      * out of sight — which defeats the reason the player moved
                      * beside the roster instead of above it. A rail row is a
                      * 96px thumb, a name and what they are on; eight fit in the
                      * same height without scrolling at all.
                      *
                      * Same list, same order, same click targets — only the
                      * density changes, so nothing about the interaction has to
                      * be relearned when the player opens.
                      */}
                    <div className="creator-team__grid creator-team__grid--rail">
                        {members.map(m => {
                            /*
                             * A live creator PLAYS here rather than navigating.
                             *
                             * The roster's whole claim is that it is one view of
                             * everyone playing this title across three platforms
                             * — and sending someone to Twitch on the first click
                             * hands that view straight back to the platform. The
                             * filter already proved they are on-game, so the
                             * stream is the payoff, not a link to it.
                             */
                            const playable = m.isLive && !m.offGame && Boolean(playerSource(m));
                            const key = memberKey(m);

                            /*
                             * The clip THIS creator has of this title, when they
                             * are off. `highlightForMember` is the shared match
                             * rule — see its doc comment for why label, and why
                             * every caller must use it rather than its own find.
                             */
                            const clip = !m.isLive ? highlightForMember(m, highlights) : undefined;

                            return (
                                <CreatorRosterCard
                                    key={key}
                                    member={m}
                                    /*
                                     * The response already carries this and the
                                     * section already reads it (see the note
                                     * above about the filter bars). The card
                                     * needs it to tell an offline member's
                                     * last-played title from a rival one.
                                     */
                                    filterGameNames={data?.filterGameNames ?? []}
                                    /* The team's authored schema, merged with this surface's additions server-side. */
                                    rosterFields={data?.rosterFields ?? []}
                                    clip={clip}
                                    playable={playable}
                                    isSelected={playable && selectedKey === key}
                                    activeHighlightId={activeHighlight?.id}
                                    onWatch={() => setSelectedKey(selectedKey === key ? undefined : key)}
                                    onPlayClip={setSelectedHighlightId}
                                    /*
                                     * Both are needed by the row's own follow
                                     * control: without onLoginRequired a
                                     * signed-out click is a silent no-op, and
                                     * without the referrer the follow loses its
                                     * attribution.
                                     */
                                    onLoginRequired={onLoginRequired}
                                    referralUserId={referralUserId}
                                />
                            );
                        })}
                    </div>
                </div>
            }

            {/*
              * The highlights row.
              *
              * Same interaction as the hero's trailer strip, and deliberately a
              * DIFFERENT thing from it: the hero strip is publisher-curated
              * official video, this is aggregated creator footage filtered to
              * the title. Keeping them as two beats matters — merged, an
              * official trailer and someone's clip would sit at equal weight.
              *
              * Only rendered when it is carrying the section, i.e. when nobody
              * is live. With a live stream on, this would be a row of recordings
              * competing with the thing actually happening.
              */}
            {highlights.length > 1 &&
                <div className="creator-team__highlights">
                    <div className="creator-team__highlights-head">
                        <h4>Highlights</h4>
                        <span>Top clips of this title from the team</span>
                    </div>
                    <div className="creator-team__highlights-row">
                        {highlights.map(h =>
                            <button
                                key={h.id}
                                type="button"
                                className={`ct-clip${h.id === activeHighlight?.id ? " ct-clip--playing" : ""}`}
                                aria-pressed={h.id === activeHighlight?.id}
                                onClick={() => setSelectedHighlightId(h.id)}
                            >
                                <span className="ct-clip__shot">
                                    {/* Twitch returns a template URL with %{width}
                                        and %{height} placeholders — unresolved,
                                        the src 404s and the row renders empty. */}
                                    <img
                                        src={h.thumbnailUrl
                                            .replace("%{width}", "320")
                                            .replace("%{height}", "180")}
                                        alt=""
                                        loading="lazy"
                                    />
                                    <span className="ct-clip__dur">{formatDuration(h.durationSeconds)}</span>
                                </span>
                                <span className="ct-clip__title">{h.title}</span>
                                <span className="ct-clip__by">
                                    {h.creatorLabel} · {compact(h.viewCount)} views
                                </span>
                            </button>)}
                    </div>
                </div>
            }

            <div className="creator-team__foot">
                {/*
                  * No live count here. The header states it, and only when it
                  * is non-zero — the footer version was a second, larger
                  * restatement of the same fact at the bottom of one section.
                  */}
                {/*
                  * No "N watching the team" either. It summed the concurrents
                  * of the members already on screen — each card states its own
                  * viewer count, so the total added a third restatement of
                  * numbers the reader can already see, under a section that
                  * had just said how many were live.
                  */}
                {/*
                  * The cross-platform claim only when it is one — i.e. when
                  * the roster actually spans more than one platform. On a
                  * single-platform roster it was just a slogan, and a slogan
                  * printed under eight offline cards is what makes a real
                  * roster read as a mock-up.
                  */}
                {rosterPlatforms.length > 1 &&
                    <span>{rosterPlatforms.join(" · ")} — one view, which no platform will ever build</span>
                }
            </div>
        </section>
    );
}
